// @ts-nocheck
import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useTeamNotes, useAddTeamNote, TeamRow } from './hooks/useEventTeamTracker';
import { format, parseISO } from 'date-fns';
import { MessageSquare } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  team: TeamRow;
  eventId: string;
  userId: string;
  userName: string;
}

export const TeamNotesDrawer: React.FC<Props> = ({ open, onClose, team, eventId, userId, userName }) => {
  const { data: notes, isLoading } = useTeamNotes(eventId, team.registration_id);
  const addNote = useAddTeamNote(eventId);
  const [newNote, setNewNote] = useState('');

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    try {
      await addNote.mutateAsync({
        event_id: eventId,
        registration_id: team.registration_id,
        team_name: team.team_name,
        coach_email: team.coach_email,
        note_text: newNote.trim(),
        created_by: userId,
        created_by_name: userName,
      });
      setNewNote('');
      toast.success('Note added');
    } catch {
      toast.error('Failed to add note');
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Notes — {team.team_name}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Notes list */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !notes || notes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No notes yet.</p>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div key={note.id} className="border rounded-lg p-3 space-y-1">
                  <p className="text-sm">{note.note_text}</p>
                  <p className="text-xs text-muted-foreground">
                    {note.created_by_name || 'Unknown'} ·{' '}
                    {format(parseISO(note.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Add note */}
          <div className="border-t pt-4 space-y-2">
            <Textarea
              placeholder="Add a note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
            />
            <Button
              onClick={handleAdd}
              disabled={!newNote.trim() || addNote.isPending}
              className="w-full"
              size="sm"
            >
              {addNote.isPending ? 'Adding...' : 'Add Note'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
